/**
 * Technology Column Definitions
 *
 * Column definitions with JSX cell renderers.
 * Separated from columns.ts to isolate JSX dependencies.
 *
 * Pattern matches:
 * - src/sections/asset/config/columnDefinitions.tsx
 * - src/sections/seeds/config/columnDefinitions.tsx
 *
 * Column widths migrated to central registry (Day 4 - 2025-10-19)
 * Location: src/config/columns/columnRegistry.ts
 *
 * Rollback instructions (if needed):
 * 1. Revert this file to previous commit
 * 2. Central registry remains (no deletion needed)
 */

import { NameColumn } from '@/components/NameColumn';
import { getColumnWidth } from '@/components/table/columns';
import { ColumnPriority, Columns } from '@/components/table/types';
import { TagsCell, VendorCell } from '@/sections/technology/components/cells';
import { parseTechnologyVersion } from '@/sections/technology/utils';
import type { Technology } from '@/types';
import { getTechnologyDisplayProduct } from '@/utils/entities/technology.util';

import {
  isColumnSortable,
  REQUIRED_COLUMN_ID,
  TECHNOLOGY_DEFAULT_COLUMN_ORDER,
} from './columns';

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

/**
 * Returns the column definitions for the technology table
 *
 * Column rendering:
 * - vendor: Plain text with copy enabled
 * - name: NameColumn with name and version as description
 * - tags: TagsCell component with overflow counter
 * - created: Date cell (First Seen)
 * - visited: Date cell (Last Seen)
 *
 * @returns Array of column configurations for Table component
 */
export const getTechnologyColumns = (): Columns<Technology> => [
  {
    label: 'Vendor',
    id: 'vendor',
    ...getColumnWidth('technology', 'vendor'),
    copy: true,
    cell: (data: Technology) => <VendorCell entity={data} />,
    sorting: isColumnSortable('vendor'),
    responsive: {
      priority: ColumnPriority.CRITICAL,
      alwaysVisible: true,
    },
  },
  {
    label: 'Technology',
    id: 'name',
    ...getColumnWidth('technology', 'name'),
    cell: (data: Technology) => (
      <NameColumn
        name={getTechnologyDisplayProduct(data)}
        description={
          data.version ? parseTechnologyVersion(data.version, true) : undefined
        }
      />
    ),
    sorting: isColumnSortable('name'),
    responsive: {
      priority: ColumnPriority.CRITICAL,
      alwaysVisible: true,
    },
  },
  {
    label: 'Tags',
    id: 'tags',
    ...getColumnWidth('technology', 'tags'),
    copy: false,
    cell: (data: Technology) => <TagsCell technology={data} />,
    sorting: false, // Tags not sortable (multi-value field)
    responsive: {
      priority: ColumnPriority.LOW,
    },
  },
  {
    label: 'First Seen',
    id: 'created',
    ...getColumnWidth('technology', 'created'),
    copy: true,
    cell: 'date',
    sorting: isColumnSortable('created'),
    responsive: {
      priority: ColumnPriority.MEDIUM,
    },
  },
  {
    label: 'Last Seen',
    id: 'visited',
    ...getColumnWidth('technology', 'visited'),
    copy: true,
    cell: 'date',
    sorting: isColumnSortable('visited'),
    responsive: {
      priority: ColumnPriority.HIGH,
    },
  },
];

// ============================================================================
// SORTABLE COLUMN CONFIGURATION
// ============================================================================

/**
 * Generates sortable columns configuration for the technology table
 *
 * Creates active and available column lists with proper defaults.
 * - The 'name' column is always required and cannot be removed (disabled: true)
 * - Default order: Vendor, Technology (name), Tags, then date columns last
 * - Users can reorder, remove (except name), and add any columns
 *
 * @returns Configuration object for useSortableColumn hook
 */
export const getSortableColumnsConfig = () => {
  const defaultColumns = getTechnologyColumns();

  // Create active columns from default order
  // All columns are removable and reorderable except 'name'
  const activeColumns = TECHNOLOGY_DEFAULT_COLUMN_ORDER.map(columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (!column) return null;

    return {
      label: column.label,
      id: column.id,
      disabled: columnId === REQUIRED_COLUMN_ID, // Only 'name' is required
      isDefault: columnId === REQUIRED_COLUMN_ID,
    };
  }).filter((item): item is NonNullable<typeof item> => item !== null);

  // Create available columns (not in default order)
  // Currently all columns are in default order, so this is empty
  const defaultOrderSet = new Set<string>(
    TECHNOLOGY_DEFAULT_COLUMN_ORDER as readonly string[]
  );
  const availableColumnIds = defaultColumns
    .map(col => col.id as string)
    .filter(id => !defaultOrderSet.has(id));

  const availableColumns = availableColumnIds
    .map(columnId => {
      const column = defaultColumns.find(col => col.id === columnId);
      if (!column) return null;

      return {
        label: column.label,
        id: column.id,
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  return {
    fixed: [],
    active: activeColumns,
    available: availableColumns,
  };
};
